// Added by Sejune Oh on 2019-07-03
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef } from 'react'
import { observer, inject } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import ScreenLock from './ScreenLock'
import { UserAuthStore } from '@/stores/user/UserAuth'
import { ReactInputChangeEvent, ReactKeyUpEvent } from '@/helper/define'
import Logger from '@/helper/Logger'
import Intl from '@/helper/Intl'
import EventStore from '@/stores/EventStore'
import ProductStore from '@/stores/ProductStore'
import CLAppStore from '@/stores/CLAppStore'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ScreenLockCProps {
  callback: () => void
  userAuth?: UserAuthStore
  globalEvt?: EventStore
  product?: ProductStore
  clApp?: CLAppStore
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'globalEvt', 'product', 'clApp')
@observer
class ScreenLockContainer extends Component<ScreenLockCProps> {
  hidePassword: boolean = true
  curPwd: string = ''
  onLogoutMode: boolean = false
  _showScreenLock: boolean = false
  _lastActiveTime?: Date

  _buttonId = 's-l-btn'
  _inputRef = createRef<HTMLInputElement>()

  constructor(props: ScreenLockCProps) {
    super(props)
    makeObservable(this, {
      hidePassword: observable,
      curPwd: observable,
      onLogoutMode: observable,
      _showScreenLock: observable,
      _lastActiveTime: observable,
      _handlePasswordInputIconClick: action,
      _handleValueChange: action,
      _clearCurPwd: action,
      _handleLogoutModeBtnClick: action,
      showScreenLock: action,
      closeScreenLock: action,
      userAuth: computed,
    })
  }

  render() {
    const options = {
      useLoginFormTopImage: this.props.product!.useLoginFormTopImage,
      productBI: this.props.product!.productBI,
      loginTopImage: this.props.product!.loginTopImage,
      loginBackgroundImage: this.props.product!.loginBackgroundImage,
    }

    if (this._showScreenLock)
      return (
        <ScreenLock
          inputRef={this._inputRef}
          onUnlockBtnClick={this._handleUnlockBtnClick}
          onLogoutBtnClick={this._handleLogoutBtnClick}
          onChangeLogoutModeBtnClick={this._handleLogoutModeBtnClick}
          userId={this.userAuth.userName!}
          onPasswordInputIconClick={this._handlePasswordInputIconClick}
          hidePassword={this.hidePassword}
          onValueChange={this._handleValueChange}
          onKeyUp={this._handleKeyUp}
          btnId={this._buttonId}
          onLogoutMode={this.onLogoutMode}
          version={process.env.REACT_APP_VER_RSC_TEXTS || 'dev'}
          copyRight={this.props.product!.copyright}
          {...options}
        />
      )

    return null
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handlePasswordInputIconClick = () => {
    this.hidePassword = !this.hidePassword
  }

  _handleValueChange: ReactInputChangeEvent = ({ currentTarget: { value, name } }) => {
    this.curPwd = value
  }

  _clearCurPwd = () => {
    this.curPwd = ''
    if (this._inputRef.current) this._inputRef.current!.value = ''
  }

  _handleLogoutModeBtnClick = (flag: boolean) => () => {
    this.onLogoutMode = flag
  }

  showScreenLock = (lastActiveTime: number) => {
    this._clearCurPwd()
    this.onLogoutMode = false
    this._showScreenLock = true
    this._lastActiveTime = new Date(lastActiveTime)
    this.props.globalEvt!.setScreenLockFlag(true)
  }

  closeScreenLock = () => {
    this._showScreenLock = false
    this.props.globalEvt!.setScreenLockFlag(false)
  }

  get userAuth() {
    return this.props.userAuth!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleUnlockBtnClick = async () => {
    try {
      const status = await this.userAuth.authenticate(this.userAuth.userName!, this.curPwd)
      if (status) {
        Logger.devLog('- Confirm Password > Authenticated.')
        this.props.callback()
        return Promise.resolve(true)
      }

      this._showErrorSnackbar('')
      return
    } catch (err) {
      this._showErrorSnackbar(err)
      return
    }
  }

  _showErrorSnackbar = (eMessage: string) => {
    this.props.globalEvt!.showEvent({
      message: this.userAuth.getSignInErrMessage(Intl, eMessage),
      evtType: 'error',
    })
  }

  _handleLogoutBtnClick = async () => {
    Logger.devLog('- Screen Lock > Logout.')
    this.props.clApp!.clearMessageBeforeWebPageUnload()
    await this.userAuth.signOut(true)
  }

  _handleKeyUp: ReactKeyUpEvent = (event) => {
    if (event.keyCode !== 13) return

    const $btn = document.getElementById(this._buttonId) as HTMLButtonElement
    $btn.click()
  }
}

export default ScreenLockContainer
